import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, DollarSign, RefreshCcw, Scale, Mail, CreditCard,
  AlertTriangle, TrendingUp, Globe, Clock, Activity, Store, Check, X, Loader2, ExternalLink, FileDown, Shield, FileSignature, Newspaper
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';
import CronHealthPill from '@/components/admin/CronHealthPill';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, subValue, color = "cyan", trend }) {
  const colorClasses = {
    cyan: 'from-[#00F0FF]/20 to-[#00F0FF]/5 border-[#00F0FF]/30 text-[#00F0FF]',
    cyan: 'from-white/20 to-white/5 border-white/30 text-white',
    yellow: 'from-[#00F0FF]/20 to-[#00F0FF]/5 border-[#00F0FF]/30 text-[#00F0FF]',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30 text-red-500',
    green: 'from-green-500/20 to-green-500/5 border-green-500/30 text-green-500',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4 md:p-6`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-6 h-6 opacity-80" />
        {trend && (
          <span className={`text-xs font-medium ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="text-2xl md:text-3xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-neutral-400">{label}</div>
      {subValue && <div className="text-xs text-neutral-500 mt-1">{subValue}</div>}
    </div>
  );
}

function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('24h');
  const [creatorApps, setCreatorApps] = useState(null);
  const [processingApp, setProcessingApp] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [staleCreators, setStaleCreators] = useState({ count: 0, creators: [] });
  const [staleExpanded, setStaleExpanded] = useState(false);
  const [purgingCreatorId, setPurgingCreatorId] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, activityRes, appsRes, staleRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/overview-stats?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/admin/admin-audit-log?limit=20`, { headers }),
        axios.get(`${API_URL}/api/admin/creator-applications`, { headers }),
        axios.get(`${API_URL}/api/admin/creators/stale`, { headers })
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data.logs || []);
      setCreatorApps(appsRes.data);
      setStaleCreators(staleRes.data || { count: 0, creators: [] });
    } catch (error) {
      console.error('Failed to load admin stats:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const approveApplication = async (applicationId) => {
    setProcessingApp(applicationId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/creator-applications/${applicationId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Creator application approved! User account created.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessingApp(null);
    }
  };

  const rejectApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    setProcessingApp(applicationId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/creator-applications/${applicationId}/reject`,
        { notes: 'Rejected from admin overview' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setProcessingApp(null);
    }
  };

  const purgeStaleCreator = async (creator) => {
    if (!window.confirm(`Release "${creator.store_name || creator.email}" Founding Creator spot?\n\nThis permanently deletes their creator profile so someone else can claim the spot. The user account is kept (they can re-apply).\n\nThis cannot be undone.`)) return;
    setPurgingCreatorId(creator.creator_id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/purge-seed-creator`,
        { creator_id: creator.creator_id, confirm: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Released ${creator.store_name || creator.email}'s spot`);
      setStaleCreators((prev) => ({
        count: Math.max(0, (prev.count || 1) - 1),
        creators: (prev.creators || []).filter((c) => c.creator_id !== creator.creator_id),
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgingCreatorId(null);
    }
  };

  const downloadInvestorReport = async () => {
    setDownloadingReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/investor-report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `theailedger-investor-report-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Investor report generated with live metrics');
    } catch (error) {
      console.error('Investor report download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate investor report');
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadNDA = async () => {
    const counterparty = window.prompt(
      "Counterparty company name (e.g. 'Acme Corp').\nLeave blank to download a fill-in-the-blank template.",
      ""
    );
    if (counterparty === null) return; // user cancelled
    setDownloadingReport(true);
    try {
      const token = localStorage.getItem('token');
      const params = counterparty.trim()
        ? `?counterparty=${encodeURIComponent(counterparty.trim())}`
        : '';
      const res = await axios.get(`${API_URL}/api/admin/nda${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const safeName = (counterparty.trim() || 'blank').replace(/\s+/g, '_');
      a.download = `theailedger-mutual-nda-${safeName}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(counterparty.trim()
        ? `NDA pre-filled for "${counterparty.trim()}" ready to send.`
        : 'Blank NDA template downloaded.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate NDA');
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadSecurityPosture = async () => {
    setDownloadingReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/security-posture`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `theailedger-security-posture-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Security posture PDF downloaded — shareable with enterprise prospects.');
    } catch (error) {
      console.error('Security posture download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate security posture');
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const badges = {
      'creator_suspended': { label: 'Suspended', color: 'bg-red-500/20 text-red-400' },
      'creator_unsuspended': { label: 'Unsuspended', color: 'bg-green-500/20 text-green-400' },
      'product_disabled': { label: 'Disabled', color: 'bg-orange-500/20 text-orange-400' },
      'product_enabled': { label: 'Enabled', color: 'bg-green-500/20 text-green-400' },
      'dispute_resolved': { label: 'Resolved', color: 'bg-blue-500/20 text-blue-400' },
      'email_resent': { label: 'Email Resent', color: 'bg-[#00F0FF]/20 text-[#00F0FF]' },
      'fraud_resolved': { label: 'Fraud Resolved', color: 'bg-yellow-500/20 text-yellow-400' },
    };
    return badges[action] || { label: action, color: 'bg-neutral-500/20 text-neutral-400' };
  };

  // Manual "Generate article now" — bypasses the Mon/Wed/Fri cron. Useful when
  // the LLM key budget was depleted and the scheduled run silently failed.
  const [articleBusy, setArticleBusy] = useState(false);
  const generateArticleNow = async () => {
    setArticleBusy(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const queueRes = await axios.post(`${API_URL}/api/admin/articles/run-cron-now`, {}, { headers });
      const jobId = queueRes.data.job_id;
      toast.success('Article queued — generating now. This usually takes 60-90s.');

      // Poll for completion (worker runs every minute)
      const start = Date.now();
      const poll = async () => {
        if (Date.now() - start > 4 * 60 * 1000) {
          toast.warning('Still running — check the blog in a couple of minutes.');
          setArticleBusy(false);
          return;
        }
        try {
          const res = await axios.get(`${API_URL}/api/admin/articles/cron-job/${jobId}`, { headers });
          const job = res.data;
          if (job.status === 'complete') {
            toast.success(`✅ Published: "${job.article_title}"`, { duration: 8000 });
            setArticleBusy(false);
            return;
          }
          if (job.status === 'failed') {
            toast.error(`Article generation failed: ${job.error || 'unknown error'}`, { duration: 10000 });
            setArticleBusy(false);
            return;
          }
          setTimeout(poll, 8000);
        } catch (_) {
          setTimeout(poll, 8000);
        }
      };
      setTimeout(poll, 8000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to queue article');
      setArticleBusy(false);
    }
  };

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Admin Overview</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-neutral-400">Monitor marketplace operations</p>
              <CronHealthPill />
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Generate article now — manual bypass when scheduler is silent (e.g. LLM budget depleted) */}
            <Button
              onClick={generateArticleNow}
              disabled={articleBusy}
              variant="outline"
              className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-semibold"
              data-testid="generate-article-now-btn"
            >
              {articleBusy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Newspaper className="w-4 h-4 mr-2" />
                  Generate article now
                </>
              )}
            </Button>

            {/* Investor Report Download */}
            <Button
              onClick={downloadInvestorReport}
              disabled={downloadingReport}
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
              data-testid="download-investor-report-btn"
            >
              {downloadingReport ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Investor Report PDF
                </>
              )}
            </Button>

            {/* Security Posture Download */}
            <Button
              onClick={downloadSecurityPosture}
              disabled={downloadingReport}
              variant="outline"
              className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-semibold"
              data-testid="download-security-posture-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Security Posture PDF
            </Button>

            {/* Mutual NDA Generator */}
            <Button
              onClick={downloadNDA}
              disabled={downloadingReport}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 font-semibold"
              data-testid="download-nda-btn"
            >
              <FileSignature className="w-4 h-4 mr-2" />
              Generate NDA
            </Button>

            {/* Period Toggle — shows a spinner on the active button while
                the underlying overview stats are refetching so the click
                gives immediate visual feedback (without it the click felt
                like a no-op even when data was refreshing). */}
            <div className="flex items-center gap-2 bg-neutral-900/50 p-1.5 rounded-lg border border-white/10">
              <Button
                variant={period === '24h' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPeriod('24h')}
                disabled={loading}
                className={`px-4 ${period === '24h' ? 'bg-white text-black hover:bg-[#00F0FF]/90' : ''}`}
                data-testid="period-toggle-24h"
              >
                {loading && period === '24h' && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Last 24h
              </Button>
              <Button
                variant={period === '7d' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPeriod('7d')}
                disabled={loading}
                className={`px-4 ${period === '7d' ? 'bg-white text-black hover:bg-[#00F0FF]/90' : ''}`}
                data-testid="period-toggle-7d"
              >
                {loading && period === '7d' && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Last 7 days
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadData()}
                disabled={loading}
                className="px-3"
                title="Refresh stats"
                data-testid="period-refresh-btn"
              >
                <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Store}
            label="Pending Applications"
            value={creatorApps?.pending_count || 0}
            color={creatorApps?.pending_count > 0 ? "cyan" : "green"}
          />
          <StatCard
            icon={ShoppingBag}
            label="Orders"
            value={stats?.orders?.total || 0}
            color="cyan"
          />
          <StatCard
            icon={DollarSign}
            label="Gross Revenue"
            value={formatCurrency(stats?.orders?.gross_revenue)}
            subValue={`Net: ${formatCurrency(stats?.orders?.net_revenue)}`}
            color="green"
          />
          <StatCard
            icon={RefreshCcw}
            label="Refunds"
            value={stats?.refunds?.count || 0}
            color="yellow"
          />
          <StatCard
            icon={Scale}
            label="Disputes"
            value={stats?.disputes?.count || 0}
            color="red"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Mail}
            label="Email Failures"
            value={stats?.email?.failed || 0}
            subValue={`${stats?.email?.retried || 0} retried`}
            color="cyan"
          />
          <StatCard
            icon={CreditCard}
            label="Checkout Failures"
            value={stats?.checkout_failures?.total || 0}
            color="red"
          />
          <StatCard
            icon={AlertTriangle}
            label="Fraud Alerts"
            value={stats?.fraud?.alerts || 0}
            color="yellow"
          />
          <StatCard
            icon={Activity}
            label="Self-Purchase Blocks"
            value={stats?.fraud?.self_purchase_blocks || 0}
            color="cyan"
          />
        </div>

        {/* Stale Creators Alert (day-21 no-upload trigger) — only renders
            when there's something to action. Zero-noise by design. */}
        {staleCreators.count > 0 && (
          <div className="bg-gradient-to-br from-red-500/10 to-yellow-500/5 border border-red-500/30 rounded-xl p-4 md:p-6" data-testid="stale-creators-panel">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-white">
                    {staleCreators.count} creator{staleCreators.count === 1 ? '' : 's'} still haven't listed a product
                  </h2>
                  <p className="text-sm text-neutral-400">
                    21+ days past onboarding. Nudge ladder completed. Release their Founding spot so someone else can claim it.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setStaleExpanded((v) => !v)}
                className="border-white/15 hover:bg-white/5 flex-shrink-0"
                data-testid="stale-creators-toggle"
              >
                {staleExpanded ? 'Hide' : 'Review'}
              </Button>
            </div>

            {staleExpanded && (
              <div className="space-y-2" data-testid="stale-creators-list">
                {staleCreators.creators.map((c) => (
                  <div
                    key={c.creator_id}
                    className="bg-black/40 rounded-xl p-4 border border-white/5 flex items-start justify-between gap-4 flex-wrap md:flex-nowrap"
                    data-testid={`stale-creator-row-${c.creator_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-white truncate">{c.store_name || c.name || c.email}</span>
                        <span className="text-xs bg-red-500/15 text-red-300 px-2 py-0.5 rounded-full">
                          {c.days_since_onboard}d stale
                        </span>
                        {c.nudge_stages_sent >= 3 && (
                          <span className="text-xs bg-white/10 text-neutral-400 px-2 py-0.5 rounded-full">
                            3/3 nudges sent
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 truncate">{c.email}</p>
                      <p className="text-xs text-neutral-600 mt-1">
                        Joined {c.created_at ? new Date(c.created_at).toLocaleDateString() : 'unknown'} · 0 products uploaded
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => purgeStaleCreator(c)}
                        disabled={purgingCreatorId === c.creator_id}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid={`release-spot-btn-${c.creator_id}`}
                      >
                        {purgingCreatorId === c.creator_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <><X className="w-4 h-4 mr-1" /> Release spot</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending Creator Applications - Inline Management */}
        {creatorApps?.applications?.filter(a => a.status === 'pending').length > 0 && (
          <div data-testid="pending-creator-applications" className="bg-gradient-to-br from-white/10 to-transparent border border-white/30 rounded-xl p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-black/20 flex items-center justify-center">
                  <Store className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Pending Creator Applications</h2>
                  <p className="text-sm text-neutral-400">Review and approve new creator signups</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">{creatorApps.pending_count || 0}</span>
            </div>
            <div className="space-y-3">
              {creatorApps.applications.filter(a => a.status === 'pending').map((app) => (
                <div key={app.application_id} data-testid={`creator-app-${app.application_id}`} className="bg-black/40 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{app.name}</span>
                        <span className="text-xs bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-0.5 rounded-full">Pending</span>
                      </div>
                      <p className="text-sm text-neutral-400">{app.email}</p>
                      <p className="text-sm text-[#00F0FF] mt-1">Store: {app.store_name}</p>
                      {app.portfolio_url && (
                        <a href={app.portfolio_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#00F0FF] hover:underline mt-1">
                          <ExternalLink className="w-3 h-3" /> Portfolio
                        </a>
                      )}
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-neutral-500">Products:</span> <span className="text-neutral-300">{app.product_description?.slice(0, 80)}</span></div>
                        <div><span className="text-neutral-500">Experience:</span> <span className="text-neutral-300">{app.experience?.slice(0, 80)}</span></div>
                      </div>
                      <p className="text-xs text-neutral-600 mt-2">
                        Submitted: {new Date(app.submitted_at).toLocaleDateString()} {app.location?.city && `from ${app.location.city}, ${app.location.country}`}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        data-testid={`approve-btn-${app.application_id}`}
                        onClick={() => approveApplication(app.application_id)}
                        disabled={processingApp === app.application_id}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        {processingApp === app.application_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`reject-btn-${app.application_id}`}
                        onClick={() => rejectApplication(app.application_id)}
                        disabled={processingApp === app.application_id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checkout Failures by Country */}
        {stats?.checkout_failures?.by_country?.length > 0 && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-lg font-semibold">Checkout Failures by Country</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {stats.checkout_failures.by_country.map((item, idx) => (
                <div key={`cf-${item._id || 'unknown'}-${idx}`} className="bg-black/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold">{item.count}</div>
                  <div className="text-sm text-neutral-400">{item._id || 'Unknown'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-white" />
            <h2 className="text-lg font-semibold">Recent Admin Activity</h2>
          </div>
          
          {recentActivity.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              No recent activity
            </div>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((log, idx) => {
                const badge = getActionBadge(log.action);
                return (
                  <div key={log._id || log.log_id || `act-${log.timestamp || idx}-${log.target_id || ''}`} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                      <div>
                        <div className="text-sm font-medium">
                          {log.target_type}: {log.target_id?.slice(0, 16)}...
                        </div>
                        <div className="text-xs text-neutral-400">
                          by {log.admin_email || log.admin_id}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500">
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminOverview;
